import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import ListItemText from '@mui/material/ListItemText';
import ToggleButton from '@mui/material/ToggleButton';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { fDateTime } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import { JobApplication } from 'src/services/applications/applications.types';
import { useGetJobCandidatesQuery } from 'src/services/applications/applications.service';
import {
  useGetJobPresentationPacksQuery,
  useGetPresentationTemplatesQuery,
  useCreatePresentationPackMutation,
  useGetPresentationPackDownloadMutation,
} from 'src/services/presentations/presentations.service';
import {
  COMPARISON_ROWS,
  ComparisonRowKey,
  PresentationPack,
  MAX_PACK_CANDIDATES,
  PresentationErrorCodes,
  PresentationExportFormat,
  MAX_COMPARISON_CANDIDATES,
} from 'src/services/presentations/presentations.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import { withReason } from '../hooks/api-error-reason';
import { downloadFromUrl } from '../hooks/use-presentation-preview';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: VoidFunction;
  jobId: string;
  jobTitle?: string;
  /** The application the dialog was opened from; that candidate starts selected. */
  initialApplicationId?: string;
  /** A shortlist pack (a section each) or a comparison (a column each, C6). */
  initialKind?: PackKind;
}

type PackKind = 'shortlist' | 'comparison';

const LIMITS: Record<PackKind, { min: number; max: number }> = {
  shortlist: { min: 1, max: MAX_PACK_CANDIDATES },
  comparison: { min: 2, max: MAX_COMPARISON_CANDIDATES },
};

const STATUS_COLOR = { pending: 'info', ready: 'success', needs_review: 'warning', failed: 'error' } as const;

const nameOf = (application: JobApplication) => [application.attached_profile?.first_name, application.attached_profile?.last_name].filter(Boolean).join(' ');

/**
 * A shortlist pack from the job (JT1-1887, C2): pick candidates on the job and their order, then the
 * worker makes one document with a shared cover and a section per candidate. Each candidate's own
 * presentation for the job is used; one is made from the template for anyone without one. The job's
 * recent packs are listed below, with the same findings confirmation as a single export.
 *
 * A comparison (C6) takes the same candidates side by side instead, a column each, with the rows the
 * recruiter picks in the order they set.
 */
export default function ShortlistPackDialog({ open, onClose, jobId, jobTitle, initialApplicationId, initialKind = 'shortlist' }: Props) {
  const { t } = useTranslate();
  const [kind, setKind] = useState<PackKind>(initialKind);
  const [rowOrder, setRowOrder] = useState<ComparisonRowKey[]>([...COMPARISON_ROWS]);
  const [rowsOff, setRowsOff] = useState<ComparisonRowKey[]>([]);
  const [selected, setSelected] = useState<string[]>(initialApplicationId ? [initialApplicationId] : []);
  const [templateKey, setTemplateKey] = useState('');
  const [format, setFormat] = useState<PresentationExportFormat>(PresentationExportFormat.PDF);
  const [anonymise, setAnonymise] = useState(false);
  const [title, setTitle] = useState('');
  const [poll, setPoll] = useState(false);

  const { currentData: candidates, isFetching } = useGetJobCandidatesQuery({ jobId, params: { page: 1, per_page: 100 } }, { skip: !open });
  const { currentData: templates } = useGetPresentationTemplatesQuery(undefined, { skip: !open });
  const { currentData: packs } = useGetJobPresentationPacksQuery(jobId, { skip: !open, pollingInterval: poll ? 5000 : 0 });
  const [createPack, { isLoading: isCreating }] = useCreatePresentationPackMutation();

  // Only candidates the agency has a record for can be presented.
  const eligible = useMemo(() => (candidates?.results ?? []).filter((application) => !!application.org_candidate?.id), [candidates]);
  const byId = useMemo(() => new Map(eligible.map((application) => [application.id, application])), [eligible]);
  const ordered = selected.filter((id) => byId.has(id));
  const { min, max } = LIMITS[kind];
  const rows = rowOrder.filter((row) => !rowsOff.includes(row));

  useEffect(() => {
    setPoll(!!packs?.some((pack) => pack.status === 'pending'));
  }, [packs]);

  useEffect(() => {
    const preferred = templates?.find((template) => template.is_default) ?? templates?.[0];
    if (preferred && !templateKey) {
      setTemplateKey(preferred.key);
      setAnonymise(!!preferred.anonymise_by_default);
    }
  }, [templates, templateKey]);

  const toggle = (id: string) => {
    setSelected((current) => {
      if (current.includes(id)) return current.filter((value) => value !== id);
      return current.length >= max ? current : [...current, id];
    });
  };

  const move = (index: number, by: -1 | 1) => {
    setSelected((current) => {
      const next = current.filter((id) => byId.has(id));
      const target = index + by;
      if (target < 0 || target >= next.length) return next;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const moveRow = (index: number, by: -1 | 1) => {
    setRowOrder((current) => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return next;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggleRow = (row: ComparisonRowKey) => {
    setRowsOff((current) => (current.includes(row) ? current.filter((value) => value !== row) : [...current, row]));
  };

  const handleCreate = async () => {
    try {
      await createPack({
        job_id: jobId,
        kind,
        comparison_rows: kind === 'comparison' ? rows : undefined,
        items: ordered.map((id) => ({ organization_candidate_id: byId.get(id)!.org_candidate!.id, application_id: id })),
        template_key: templateKey || undefined,
        format,
        title: title.trim() || undefined,
        anonymise,
      }).unwrap();
      enqueueSnackbar(t('presentations.packs.started'));
      setPoll(true);
    } catch (error) {
      enqueueSnackbar(withReason(t('presentations.packs.failed'), error), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t(kind === 'comparison' ? 'presentations.packs.comparison_title' : 'presentations.packs.title')}
        {jobTitle && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{jobTitle}</Typography>}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={kind}
            onChange={(_event, next: PackKind | null) => next && setKind(next)}
            aria-label={t('presentations.packs.kind')}
          >
            <ToggleButton value="shortlist">{t('presentations.packs.kind_shortlist')}</ToggleButton>
            <ToggleButton value="comparison">{t('presentations.packs.kind_comparison')}</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('presentations.packs.candidates')}</Typography>
              <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, maxHeight: 280, overflowY: 'auto' }}>
                {!isFetching && !eligible.length && (
                  <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>{t('presentations.packs.no_candidates')}</Typography>
                )}
                <List dense disablePadding>
                  {eligible.map((application) => {
                    const checked = selected.includes(application.id);
                    const full = !checked && ordered.length >= max;
                    return (
                      <ListItem key={application.id} disablePadding sx={{ px: 1 }}>
                        <FormControlLabel
                          sx={{ flex: 1, m: 0, minWidth: 0 }}
                          control={<Checkbox size="small" checked={checked} disabled={full} onChange={() => toggle(application.id)} />}
                          label={<ListItemText primary={nameOf(application)} secondary={application.attached_profile?.position_title} primaryTypographyProps={{ noWrap: true }} secondaryTypographyProps={{ noWrap: true }} />}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t(kind === 'comparison' ? 'presentations.packs.comparison_limit' : 'presentations.packs.limit', { count: max, min })}</Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('presentations.packs.order')}</Typography>
              <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, maxHeight: 280, overflowY: 'auto' }}>
                {!ordered.length && <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>{t('presentations.packs.order_empty')}</Typography>}
                <List dense disablePadding>
                  {ordered.map((id, index) => {
                    const application = byId.get(id)!;
                    return (
                      <ListItem
                        key={id}
                        sx={{ pr: 11 }}
                        secondaryAction={(
                          <>
                            <IconButton size="small" aria-label={t('presentations.packs.move_up')} disabled={index === 0} onClick={() => move(index, -1)}>
                              <Iconify icon="eva:arrow-upward-fill" width={18} />
                            </IconButton>
                            <IconButton size="small" aria-label={t('presentations.packs.move_down')} disabled={index === ordered.length - 1} onClick={() => move(index, 1)}>
                              <Iconify icon="eva:arrow-downward-fill" width={18} />
                            </IconButton>
                          </>
                        )}
                      >
                        <ListItemText primary={`${index + 1}. ${nameOf(application)}`} primaryTypographyProps={{ noWrap: true }} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </Stack>

          {ordered.length > max && <Alert severity="warning">{t('presentations.packs.too_many', { count: max })}</Alert>}

          {kind === 'comparison' && (
            <Box>
              <Typography variant="subtitle2">{t('presentations.packs.rows')}</Typography>
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mb: 1 }}>{t('presentations.packs.rows_helper')}</Typography>
              <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                {rowOrder.map((row, index) => (
                  <ListItem
                    key={row}
                    sx={{ pr: 11 }}
                    secondaryAction={(
                      <>
                        <IconButton size="small" aria-label={t('presentations.packs.row_up', { row: t(`presentations.packs.row_labels.${row}`) })} disabled={index === 0} onClick={() => moveRow(index, -1)}>
                          <Iconify icon="eva:arrow-upward-fill" width={18} />
                        </IconButton>
                        <IconButton size="small" aria-label={t('presentations.packs.row_down', { row: t(`presentations.packs.row_labels.${row}`) })} disabled={index === rowOrder.length - 1} onClick={() => moveRow(index, 1)}>
                          <Iconify icon="eva:arrow-downward-fill" width={18} />
                        </IconButton>
                      </>
                    )}
                  >
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={<Checkbox size="small" checked={!rowsOff.includes(row)} onChange={() => toggleRow(row)} />}
                      label={<Typography variant="body2">{t(`presentations.packs.row_labels.${row}`)}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              label={t('presentations.packs.template')}
              value={templates?.some((template) => template.key === templateKey) ? templateKey : ''}
              onChange={(event) => setTemplateKey(event.target.value)}
              helperText={t('presentations.packs.template_helper')}
            >
              {(templates ?? []).map((template) => (
                <MenuItem key={template.key} value={template.key}>{template.name}</MenuItem>
              ))}
            </TextField>

            <TextField select fullWidth label={t('presentations.export.format')} value={format} onChange={(event) => setFormat(event.target.value as PresentationExportFormat)}>
              <MenuItem value={PresentationExportFormat.PDF}>{t('presentations.packs.pdf')}</MenuItem>
              <MenuItem value={PresentationExportFormat.DOCX}>{t('presentations.packs.docx')}</MenuItem>
              <MenuItem value={PresentationExportFormat.PPTX}>{t('presentations.packs.pptx')}</MenuItem>
            </TextField>
          </Stack>

          <TextField
            fullWidth
            label={t('presentations.packs.title_label')}
            placeholder={jobTitle ? t(kind === 'comparison' ? 'presentations.packs.comparison_placeholder' : 'presentations.packs.title_placeholder', { job: jobTitle }) : undefined}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            inputProps={{ maxLength: 200 }}
          />

          <FormControlLabel
            control={<Switch checked={anonymise} onChange={(event) => setAnonymise(event.target.checked)} />}
            label={(
              <Box>
                <Typography variant="subtitle2">{t('presentations.anonymise.toggle')}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.packs.anonymise_helper')}</Typography>
              </Box>
            )}
          />

          {!!packs?.length && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('presentations.packs.recent')}</Typography>
                <Stack spacing={1}>
                  {packs.map((pack) => <PackRow key={pack.id} pack={pack} />)}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>{t('common.close')}</Button>
        <LoadingButton variant="contained" loading={isCreating} disabled={ordered.length < min || ordered.length > max || !templates || (kind === 'comparison' && !rows.length)} onClick={handleCreate}>
          {t(kind === 'comparison' ? 'presentations.packs.comparison_submit' : 'presentations.packs.submit', { count: ordered.length })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

type ApiError = { data?: { error_code?: string } };

/** One pack: its status, and its file once it's made. A flagged pack needs the findings confirmed first. */
export function PackRow({ pack }: { pack: PresentationPack }) {
  const { t } = useTranslate();
  const [reviewing, setReviewing] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [getDownload, { isLoading }] = useGetPresentationPackDownloadMutation();

  const needsConfirming = pack.status === 'needs_review' && !pack.acknowledged;

  const download = async () => {
    if (needsConfirming && !acknowledged) {
      setReviewing(true);
      return;
    }
    try {
      const { url } = await getDownload({ id: pack.id, acknowledge: needsConfirming || undefined }).unwrap();
      downloadFromUrl(url, pack.file_name ?? pack.title);
    } catch (error) {
      if ((error as ApiError)?.data?.error_code === PresentationErrorCodes.ExportNotAcknowledged) {
        setReviewing(true);
      } else {
        enqueueSnackbar(t('presentations.packs.download_failed'), { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: 1.5, borderRadius: 1, border: (theme) => `1px solid ${theme.palette.divider}` }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>{pack.title}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {[t('presentations.packs.count', { count: pack.candidate_count }), pack.format.toUpperCase(), fDateTime(pack.created_at)].join(' · ')}
          </Typography>
        </Box>
        <Label variant="soft" color={STATUS_COLOR[pack.status]}>{t(`presentations.packs.status.${pack.status}`)}</Label>
        {(pack.status === 'ready' || pack.status === 'needs_review') && (
          <LoadingButton size="small" variant="outlined" loading={isLoading} disabled={reviewing && !acknowledged} onClick={download} startIcon={<Iconify icon="eva:download-fill" />}>
            {t('presentations.packs.download')}
          </LoadingButton>
        )}
      </Stack>

      {pack.status === 'failed' && pack.error && <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>{pack.error}</Typography>}

      {needsConfirming && reviewing && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('presentations.packs.findings')}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {pack.findings.map((finding, index) => (
              <li key={`${finding.candidate}-${finding.text}-${index}`}>
                <Typography variant="body2">
                  {[finding.candidate, finding.location].filter(Boolean).join(' · ')}: <strong>{finding.text}</strong>
                </Typography>
              </li>
            ))}
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox size="small" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />}
            label={<Typography variant="body2">{t('presentations.packs.acknowledge')}</Typography>}
          />
        </Alert>
      )}
    </Box>
  );
}
